import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Camera, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface JournalEntry {
  id: string;
  photoUrl: string;
  commonName: string;
  catalogSlug: string | null;
  isWeed: boolean;
  userLabel: string | null;
  createdAt: string;
}

export default function GardenJournal() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ entries: JournalEntry[] }>({
    queryKey: ["/api/garden/journal"],
    queryFn: async () => {
      const res = await fetch("/api/garden/journal", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (entryId: string) => {
      const res = await fetch(`/api/garden/journal/${entryId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete");
      return entryId;
    },
    onSuccess: () => {
      toast({ title: "Entry deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/garden/journal"] });
    },
    onError: (err: any) => {
      toast({ title: "Couldn't delete", description: String(err?.message ?? err) });
    },
  });

  const entries = data?.entries ?? [];

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 pb-24">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Plant journal</h1>
        <Button variant="outline" size="sm" onClick={() => setLocation("/garden")}>
          Back
        </Button>
      </div>

      <Button onClick={() => setLocation("/garden/diagnose")} className="w-full">
        <Camera className="mr-2 h-4 w-4" />
        Identify a new plant
      </Button>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            No entries yet. Identify a plant and it'll show up here.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <Card
              key={entry.id}
              className="cursor-pointer transition-colors hover:bg-muted/40"
              onClick={() => setLocation(`/garden/journal/${entry.id}`)}
            >
              <CardContent className="flex gap-3 p-3">
                <img
                  src={entry.photoUrl}
                  alt={entry.commonName}
                  className="h-20 w-20 flex-none rounded object-cover"
                />
                <div className="flex flex-1 flex-col justify-between">
                  <div>
                    <div className="font-medium">
                      {entry.userLabel || entry.commonName}
                    </div>
                    {entry.userLabel ? (
                      <div className="text-xs text-muted-foreground">
                        {entry.commonName}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    {entry.isWeed ? (
                      <Badge variant="destructive">Weed</Badge>
                    ) : entry.catalogSlug ? (
                      <Badge variant="secondary">In catalog</Badge>
                    ) : null}
                    <span className="text-xs text-muted-foreground">
                      {new Date(entry.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-none self-start text-muted-foreground hover:text-destructive"
                  disabled={deleteMutation.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (
                      confirm(
                        `Delete "${entry.userLabel || entry.commonName}"? This can't be undone.`,
                      )
                    ) {
                      deleteMutation.mutate(entry.id);
                    }
                  }}
                  aria-label="Delete entry"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
